import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Menu, X, ChevronDown } from "lucide-react";

const TIMMA = "https://timma.no/salong/medskin-by";

const MENU = [
  {
    label: "Hudutfordringer",
    items: [
      { href: "/hudutfordringer/akne-arr-uren-hud", label: "Akne, arr & uren hud" },
      { href: "/hudutfordringer/sensitiv-hud", label: "Sensitiv hud" },
      { href: "/hudutfordringer/rosacea-inflammasjon", label: "Rosacea & inflammasjon" },
      { href: "/hudutfordringer/hudbarriere", label: "Hudbarriere" },
      { href: "/hudutfordringer/hormonell-ubalanse", label: "Hormonell ubalanse" },
      { href: "/hudutfordringer/anti-age-hudfornyelse", label: "Anti-age & hudfornyelse" },
    ],
  },
  {
    label: "Ansiktsbehandlinger",
    items: [
      { href: "/behandlinger/hydrafacial", label: "Hydrafacial" },
      { href: "/behandlinger/dyprens", label: "Dyprens" },
      { href: "/behandlinger/kjemisk-peeling", label: "Kjemisk peeling" },
      { href: "/behandlinger/led-lysbehandling", label: "LED-lysbehandling" },
      { href: "/behandlinger/beroligende-ansiktsbehandling", label: "Beroligende behandling" },
    ],
  },
  {
    label: "Hudforbedring",
    items: [
      { href: "/behandlinger/dermapen", label: "Dermapen" },
      { href: "/behandlinger/microneedling", label: "Microneedling" },
      { href: "/behandlinger/subcision", label: "Subcision" },
      { href: "/behandlinger/plasma-skin-tightening", label: "Plasma" },
    ],
  },
  {
    label: "Injeksjon & Anti-age",
    items: [
      { href: "/behandlinger/skinbooster", label: "Skinboosters" },
      { href: "/behandlinger/profhilo", label: "Profhilo" },
      { href: "/behandlinger/polynukleotider", label: "Polynukleotider" },
      { href: "/behandlinger/mesoterapi", label: "Mesoterapi" },
      { href: "/behandlinger/prp", label: "PRP" },
      { href: "/behandlinger/filler", label: "Filler" },
      { href: "/behandlinger/sculptra", label: "Sculptra" },
      { href: "/behandlinger/hifu", label: "HIFU" },
    ],
  },
  {
    label: "Vipper & Bryn",
    items: [
      { href: "/behandlinger/vippeloft", label: "Vippeløft" },
      { href: "/behandlinger/brow-lamination", label: "Brow Lamination" },
      { href: "/behandlinger/browshape", label: "Browshape" },
      { href: "/behandlinger/farging-vipper", label: "Farging av vipper" },
      { href: "/behandlinger/vippeekstensjoner", label: "Vippeekstensjoner" },
    ],
  },
  {
    label: "Permanent Makeup",
    items: [
      { href: "/behandlinger/powder-brows", label: "Powder brows" },
      { href: "/behandlinger/microblading", label: "Microblading" },
      { href: "/behandlinger/pmu-refill", label: "Refill" },
      { href: "/behandlinger/pmu-touch-up", label: "Touch up" },
    ],
  },
  {
    label: "Tannbleking",
    href: "/behandlinger/tannbleking",
  },
];

export const Nav = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [openIdx, setOpenIdx] = useState(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <motion.header
      initial={{ y: -24, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.7, ease: "easeOut" }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled ? "backdrop-blur-xl bg-[#F7F6F3]/85 border-b border-[#E5E0DA]" : "backdrop-blur-md bg-[#F7F6F3]/60"
      }`}
      data-testid="site-nav"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 h-20 flex items-center justify-between gap-8">
        <Link to="/" className="flex items-baseline gap-2 shrink-0" data-testid="logo-link">
          <span className="font-serif text-2xl md:text-[1.7rem] tracking-tight text-[#2C2C2A]">Medskinby</span>
          <span className="text-[10px] uppercase tracking-[0.25em] text-[#635D56] hidden sm:inline">Oslo</span>
        </Link>

        <nav className="hidden lg:flex items-center gap-5 xl:gap-7 flex-1 justify-center">
          {MENU.map((m, i) => {
            // Direct link (no dropdown)
            if (m.href) {
              return (
                <Link
                  key={m.label}
                  to={m.href}
                  className="text-[11px] uppercase tracking-[0.18em] xl:tracking-[0.22em] text-[#2C2C2A] hover:text-[#C4A981] transition-colors duration-300"
                  data-testid={`nav-menu-${i}`}
                >
                  {m.label}
                </Link>
              );
            }
            // Dropdown
            return (
              <div
                key={m.label}
                className="relative"
                onMouseEnter={() => setOpenIdx(i)}
                onMouseLeave={() => setOpenIdx(null)}
              >
                <button
                  className="flex items-center gap-1.5 text-[11px] uppercase tracking-[0.18em] xl:tracking-[0.22em] text-[#2C2C2A] hover:text-[#C4A981] transition-colors duration-300 py-7"
                  onClick={() => setOpenIdx((v) => (v === i ? null : i))}
                  aria-expanded={openIdx === i}
                  aria-haspopup="true"
                  data-testid={`nav-menu-${i}`}
                >
                  {m.label}
                  <ChevronDown size={12} strokeWidth={1.5} className={`transition-transform ${openIdx === i ? "rotate-180" : ""}`} />
                </button>
                {openIdx === i && (
                  <div className="absolute top-full left-1/2 -translate-x-1/2 pt-2 z-50">
                    <div className="bg-[#F7F6F3] border border-[#E5E0DA] rounded-2xl shadow-[0_30px_60px_-30px_rgba(74,66,56,0.35)] p-4 min-w-[260px]">
                      {m.items.map((it) => (
                        <Link
                          key={it.href}
                          to={it.href}
                          className="block px-4 py-3 rounded-xl text-sm text-[#2C2C2A] hover:bg-[#EBE5DF] hover:text-[#4A4238] transition-all duration-200 font-light"
                          onClick={() => setOpenIdx(null)}
                          data-testid={`nav-dropdown-${it.href.replace(/\//g, "-")}`}
                        >
                          {it.label}
                        </Link>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </nav>

        <a
          href={TIMMA}
          target="_blank"
          rel="noopener noreferrer"
          className="hidden lg:inline-flex items-center gap-2 bg-[#4A4238] text-[#F7F6F3] hover:bg-[#2C2C2A] transition-all duration-300 rounded-full px-5 py-2.5 text-[11px] uppercase tracking-[0.22em] shrink-0"
          data-testid="nav-book-btn"
        >
          Book time
        </a>

        <button
          className="lg:hidden text-[#2C2C2A]"
          onClick={() => setOpen((v) => !v)}
          data-testid="mobile-menu-toggle"
          aria-label="Toggle menu"
        >
          {open ? <X size={22} strokeWidth={1.2} /> : <Menu size={22} strokeWidth={1.2} />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden border-t border-[#E5E0DA] bg-[#F7F6F3]/95 backdrop-blur-xl max-h-[80vh] overflow-y-auto" data-testid="mobile-menu">
          <div className="px-6 py-6 space-y-5">
            {MENU.map((m) => {
              // Direct link mobile (no items)
              if (m.href) {
                return (
                  <Link
                    key={m.label}
                    to={m.href}
                    onClick={() => setOpen(false)}
                    className="block text-sm uppercase tracking-[0.2em] text-[#2C2C2A] border-b border-[#E5E0DA] pb-4"
                  >
                    {m.label}
                  </Link>
                );
              }
              return (
                <details key={m.label} className="group border-b border-[#E5E0DA] pb-4">
                  <summary className="flex items-center justify-between text-sm uppercase tracking-[0.2em] text-[#2C2C2A] cursor-pointer list-none">
                    {m.label}
                    <ChevronDown size={14} strokeWidth={1.5} className="group-open:rotate-180 transition-transform" />
                  </summary>
                  <ul className="mt-4 space-y-3 pl-1">
                    {m.items.map((it) => (
                      <li key={it.href}>
                        <Link
                          to={it.href}
                          onClick={() => setOpen(false)}
                          className="text-sm text-[#635D56] hover:text-[#4A4238]"
                        >
                          {it.label}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </details>
              );
            })}
            <a
              href={TIMMA}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => setOpen(false)}
              className="mt-2 inline-flex w-fit items-center gap-2 bg-[#4A4238] text-[#F7F6F3] rounded-full px-6 py-3 text-xs uppercase tracking-[0.22em]"
              data-testid="mobile-book-btn"
            >
              Book time
            </a>
          </div>
        </div>
      )}
    </motion.header>
  );
};
